import { Box, createStandaloneToast, Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import { themeColors } from 'themes/clients/baseTheme';
import { colors } from 'themes/tokens';

import { CheckOutlined, Cross, NeedAttention } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

const STATUS_COLORS = {
  success: {
    textIconColor: themeColors.functional['44812F'],
    bgColor: themeColors.feedback.DBF2D3,
    buttonColor: colors['#44812F'],
  },
  error: {
    textIconColor: themeColors.functional.C62C02,
    bgColor: themeColors.feedback.FFE1D9,
    buttonColor: colors['#C62C02'],
  },
  info: {
    textIconColor: themeColors.functional['333333'],
    bgColor: '#FFF',
    buttonColor: colors['#333333'],
  },
  warning: {
    textIconColor: themeColors.functional['333333'],
    bgColor: '#FFF',
    buttonColor: colors['#333333'],
  },
};

const STATUS_ICONS = {
  success: CheckOutlined,
  error: NeedAttention,
  info: CheckOutlined,
  warning: NeedAttention,
};

export const useToast = () => {
  const theme = useTheme();
  /* 
    If you're using a custom theme, you must pass it in the arguments to createStandaloneToast. 
    If you don't, the default theme will be applied, causing the font size of your page to 
    shift when the toast is open.
    REFER: https://v2.chakra-ui.com/docs/components/toast/usage#standalone-toasts

    also we are not exporting the <ToastContainer /> from createStandaloneToast as suggested in the doc.
    Because doing so will cause the toast to be rendered twice.
    REFER: https://github.com/chakra-ui/chakra-ui/issues/7547#issuecomment-1499226350
  */
  const { toast } = createStandaloneToast({ theme });

  return ({
    title = '',
    description = '',
    status = 'success',
    duration = 5000,
    isClosable = true,
    rightComp = null,
    hideLeftIcon = false,
    customStartIcon = null,
    ...rest
  }) => {
    const { textIconColor, bgColor, buttonColor } = STATUS_COLORS[status];
    const Icon = customStartIcon ?? STATUS_ICONS[status];
    toast({
      position: 'bottom-left',
      render: ({ onClose }) => (
        <Flex
          p={3}
          bg={bgColor}
          display="flex"
          justifyContent="space-between"
          borderRadius="4px"
          boxShadow={theme.shadows.large}
          minH="60px"
          alignItems="center"
          maxW={isMobileView() ? '80%' : 'none'}
        >
          <Flex flexGrow={1} alignItems="center">
            {!hideLeftIcon && <Icon boxSize="36px" color={textIconColor} />}
            <TextBodyMedium ml={2} color={textIconColor}>
              {title}
            </TextBodyMedium>
          </Flex>
          <Flex>
            {rightComp}
            {isClosable && (
              <Box display="flex" alignItems="center" onClick={onClose} cursor="pointer" p={2} h="40px">
                <Cross color={buttonColor} m={1} boxSize="20px" />
              </Box>
            )}
          </Flex>
        </Flex>
      ),
      duration,
      isClosable,
      status,
      description,
      ...rest,
    });
  };
};
